import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { getCoverageCodeLabel } from '../../../data/coverageCodes';
import { formatDateOrNA } from '../../../utils/dateUtils';

const NON_MEDICAL_CLAIM_TYPES = new Set(['CANCELLATION', 'BAGGAGE']);

export interface PreviouslySubmittedExpense {
  id?: string;
  description?: string;
  /** Pre-resolved label — takes precedence over description/coverageCode when present. */
  service?: string;
  coverageCode?: string;
  serviceStartDate?: string;
  serviceDate?: string;
  purchaseDate?: string;
  serviceProvider?: string;
  amountCAD?: number;
  amount?: number;
}

export interface PreviouslySubmittedExpensesProps {
  expenses: PreviouslySubmittedExpense[];
  refundReceivedAmount?: number;
  /** Title override. Defaults to a localized "Liste des dépenses" / "Frais déjà soumis" label per variant. */
  title?: React.ReactNode;
  /** When true, the component renders nothing if the expenses list is empty. Defaults to false. */
  hideIfEmpty?: boolean;
  /**
   * DEV-6963 — claim flow type (MEDICAL / CANCELLATION / BAGGAGE). When set to a
   * non-medical value, the service column displays a fixed localized label
   * ("Frais réclamés" / "Claimed expenses") regardless of what the backend put in
   * `description` / `coverageCode`. Reason: for cancellation and baggage the
   * user-entered description is not (yet) persisted to NOVA, so the NovaToPortal
   * mapper falls back to the NOVA enum value (`Trip_Cancellation_Type` /
   * `Luggage_Situation_Declared`) which is meaningless to the insured.
   */
  claimType?: string | null;
  /**
   * Visual variant.
   * - `'claim-detail'` (default): the original claim-detail Expenses tab design — borderless
   *   section, h2 title, uppercase column headers, tenant-coloured service labels, separate
   *   grey footer card with subtotal/refund/total, "$XXX.XX CAD" amount format.
   * - `'append-expenses'`: the bordered-card layout used inside the append-expenses validation
   *   screen (`04E-02-VALIDATION`) and its mobile variant.
   */
  variant?: 'claim-detail' | 'append-expenses';
}

const resolveServiceDate = (expense: PreviouslySubmittedExpense, language: string): string => {
  const raw = expense.serviceStartDate || expense.serviceDate || expense.purchaseDate;
  if (!raw) return 'N/A';
  return formatDateOrNA(raw, language === 'fr' ? 'fr-CA' : 'en-CA');
};

const resolveServiceLabel = (
  expense: PreviouslySubmittedExpense,
  language: string,
  claimType: string | null | undefined,
  t: TFunction
): string => {
  if (claimType && NON_MEDICAL_CLAIM_TYPES.has(claimType.toUpperCase())) {
    return t('claimDetails.expenses.nonMedicalServiceLabel');
  }
  if (expense.service && expense.service.trim() !== '') {
    return expense.service;
  }
  if (expense.coverageCode) {
    const label = getCoverageCodeLabel(expense.coverageCode, language);
    if (label) {
      return label;
    }
  }
  if (expense.description && expense.description.trim() !== '') {
    return expense.description;
  }
  return expense.coverageCode || 'N/A';
};

const resolveAmountCAD = (expense: PreviouslySubmittedExpense): number => {
  if (typeof expense.amountCAD === 'number') {
    return expense.amountCAD;
  }
  if (typeof expense.amount === 'number') {
    return expense.amount;
  }
  return 0;
};

const formatCAD = (value: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(value);

/**
 * `$1,100.00 CAD` format used by the claim-detail variant — matches the legacy design.
 * Negative values render as `-$250.00 CAD`.
 */
const formatCadWithSuffix = (value: number): string => {
  const abs = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return `${value < 0 ? '-' : ''}$${abs} CAD`;
};

interface LayoutProps {
  expenses: PreviouslySubmittedExpense[];
  refundReceivedAmount: number;
  title?: React.ReactNode;
  claimType?: string | null;
  language: string;
  t: TFunction;
}

/**
 * Claim-detail Expenses tab design — borderless section, uppercase column headers,
 * tenant-coloured service labels, separate grey footer card.
 */
const ClaimDetailLayout: React.FC<LayoutProps> = ({
  expenses,
  refundReceivedAmount,
  title,
  claimType,
  language,
  t,
}) => {
  const subtotal = expenses.reduce((acc, expense) => acc + resolveAmountCAD(expense), 0);
  const total = Math.max(0, subtotal - refundReceivedAmount);
  const headerTitle = title ?? t('claimDetails.expenses.listTitle', 'Liste des dépenses');

  return (
    <section
      className="mb-8"
      aria-label={typeof headerTitle === 'string' ? headerTitle : undefined}
    >
      <h2 className="text-xl font-semibold text-gray-900 mb-4">{headerTitle}</h2>

      {expenses.length === 0 ? (
        <div className="p-4 text-sm text-gray-600 bg-gray-50 rounded-md">
          {t('appendExpenses.previouslySubmittedSection.empty', 'Aucun frais déjà soumis.')}
        </div>
      ) : (
        <>
          {/* Mobile: card view */}
          <div className="md:hidden space-y-3 mb-6">
            {expenses.map((expense, index) => (
              <div
                key={expense.id ?? index}
                className="border-b border-gray-200 pb-3"
              >
                <p className="font-medium text-tenant-700">
                  {resolveServiceLabel(expense, language, claimType, t)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{resolveServiceDate(expense, language)}</p>
                {expense.serviceProvider && (
                  <p className="text-sm text-gray-600 mt-1">{expense.serviceProvider}</p>
                )}
                <p className="text-right font-semibold text-gray-900 mt-2">
                  {formatCadWithSuffix(resolveAmountCAD(expense))}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop: table view */}
          <div className="hidden md:block overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-gray-500">
                    {t('claimDetails.expenses.columns.service', 'Frais')}
                  </th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-gray-500">
                    {t('claimDetails.expenses.columns.date', 'Date')}
                  </th>
                  <th className="px-4 py-3 font-medium text-xs uppercase tracking-wider text-gray-500 text-right">
                    {t('claimDetails.expenses.columns.amountClaimed', 'Montant réclamé')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense, index) => (
                  <tr key={expense.id ?? index} className="border-b border-gray-100">
                    <td className="px-4 py-3 text-tenant-700 font-medium">
                      {resolveServiceLabel(expense, language, claimType, t)}
                      {expense.serviceProvider && (
                        <span className="block text-gray-500 text-xs font-normal">{expense.serviceProvider}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{resolveServiceDate(expense, language)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">
                      {formatCadWithSuffix(resolveAmountCAD(expense))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-700">
                {t('claimDetails.expenses.expensesSubtotal', 'Sous-total des frais')}
              </span>
              <span className="text-gray-900">{formatCadWithSuffix(subtotal)}</span>
            </div>
            {refundReceivedAmount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>
                  {t('claimDetails.expenses.refundReceived', 'Remboursement ou crédit reçu')}
                </span>
                <span>{formatCadWithSuffix(-refundReceivedAmount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-200 text-base font-bold">
              <span className="text-gray-900">
                {t('claimDetails.expenses.total', 'Total')}
              </span>
              <span className="text-gray-900">{formatCadWithSuffix(total)}</span>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

/**
 * Append-expenses validation screen design — bordered card with a coloured header row,
 * inline footer, "CA$X,XXX.XX" amount format. Preserved verbatim from the pre-DEV-6963
 * layout so the 04E-02-VALIDATION read-only "previously submitted" section keeps its
 * existing look.
 */
const AppendExpensesLayout: React.FC<LayoutProps> = ({
  expenses,
  refundReceivedAmount,
  title,
  claimType,
  language,
  t,
}) => {
  const subtotal = expenses.reduce((acc, expense) => acc + resolveAmountCAD(expense), 0);
  const total = Math.max(0, subtotal - refundReceivedAmount);
  const headerTitle =
    title ?? t('appendExpenses.previouslySubmittedSection.title', 'Frais déjà soumis');

  return (
    <section
      className="mb-8 border border-gray-200 rounded-lg bg-white"
      aria-label={typeof headerTitle === 'string' ? headerTitle : undefined}
    >
      <header className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <h2 className="text-lg font-semibold text-gray-900">{headerTitle}</h2>
      </header>

      {expenses.length === 0 ? (
        <div className="p-4 text-sm text-gray-600">
          {t('appendExpenses.previouslySubmittedSection.empty', 'Aucun frais déjà soumis.')}
        </div>
      ) : (
        <>
          {/* Mobile: card view */}
          <div className="md:hidden space-y-3 p-4">
            {expenses.map((expense, index) => (
              <div
                key={expense.id ?? index}
                className="border border-gray-200 rounded-md p-3"
              >
                <p className="font-medium text-gray-900">
                  {resolveServiceLabel(expense, language, claimType, t)}
                </p>
                <p className="text-sm text-gray-600 mt-1">{resolveServiceDate(expense, language)}</p>
                {expense.serviceProvider && (
                  <p className="text-sm text-gray-600 mt-1">{expense.serviceProvider}</p>
                )}
                <p className="text-right font-semibold text-gray-900 mt-2">
                  {formatCAD(resolveAmountCAD(expense))}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop: table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-700">
                  <th className="px-4 py-2 font-medium">
                    {t('appendExpenses.previouslySubmittedSection.columns.service', 'Service')}
                  </th>
                  <th className="px-4 py-2 font-medium">
                    {t('appendExpenses.previouslySubmittedSection.columns.date', 'Date')}
                  </th>
                  <th className="px-4 py-2 font-medium text-right">
                    {t('appendExpenses.previouslySubmittedSection.columns.amount', 'Montant')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense, index) => (
                  <tr key={expense.id ?? index} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-900">
                      {resolveServiceLabel(expense, language, claimType, t)}
                      {expense.serviceProvider && (
                        <span className="block text-gray-500 text-xs">{expense.serviceProvider}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-gray-700">{resolveServiceDate(expense, language)}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-900">
                      {formatCAD(resolveAmountCAD(expense))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className="border-t border-gray-200 bg-gray-50 px-4 py-3 rounded-b-lg space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">
                {t('appendExpenses.previouslySubmittedSection.subtotal', 'Sous-total')}
              </span>
              <span className="text-gray-900">{formatCAD(subtotal)}</span>
            </div>
            {refundReceivedAmount > 0 && (
              <div className="flex justify-between text-red-600">
                <span>
                  {t('appendExpenses.previouslySubmittedSection.refund', 'Remboursement reçu')}
                </span>
                <span>- {formatCAD(refundReceivedAmount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-gray-200 mt-1 font-semibold">
              <span className="text-gray-900">
                {t('appendExpenses.previouslySubmittedSection.total', 'Total')}
              </span>
              <span className="text-gray-900">{formatCAD(total)}</span>
            </div>
          </footer>
        </>
      )}
    </section>
  );
};

/**
 * Read-only summary of expenses previously submitted on a reference claim.
 *
 * Renders one of two layouts depending on `variant`:
 * - `'claim-detail'` (default): original Expenses-tab design on `ClaimDetailsScreen`
 *   (`00-10-RECLAMATIONS`). Borderless section + uppercase headers + tenant-coloured service
 *   labels + separate grey footer card + "$X.XX CAD" amounts.
 * - `'append-expenses'`: bordered-card layout used in the append-expenses validation screen
 *   (`04E-02-VALIDATION`) for the read-only "previously submitted expenses" section. Preserved
 *   verbatim from the pre-DEV-6963 design.
 */
const PreviouslySubmittedExpenses: React.FC<PreviouslySubmittedExpensesProps> = ({
  expenses,
  refundReceivedAmount = 0,
  title,
  hideIfEmpty = false,
  claimType,
  variant = 'claim-detail',
}) => {
  const { t, i18n } = useTranslation();

  if (hideIfEmpty && expenses.length === 0) {
    return null;
  }

  const layoutProps: LayoutProps = {
    expenses,
    refundReceivedAmount,
    title,
    claimType,
    language: i18n.language,
    t,
  };

  return variant === 'append-expenses' ? (
    <AppendExpensesLayout {...layoutProps} />
  ) : (
    <ClaimDetailLayout {...layoutProps} />
  );
};

export default PreviouslySubmittedExpenses;
